import { cloneDeep } from 'lodash';

import {
  WidgetAttributeDTO,
  WidgetAttributeDTOTypeEnum,
  WidgetDTO,
  WidgetDTOTypeEnum,
} from '@neptune/shared/core-apis-leaderboard-domain';
import { parseCustomAxis } from '@neptune/shared/custom-expressions-domain';
import {
  type AttributePatternWidgetSource,
  type AttributeWidgetSource,
  type CustomYExpressionWidgetSource,
  isAttributePatternSource,
  isYCustomExpressionSource,
  type NamespaceWidgetSource,
  TextWidgetSource,
  type Widget,
  WidgetSourceType,
} from '@neptune/shared/widgets-domain';
import {
  assertVegaLiteFloatSeriesConfiguration,
  decodeVegaLiteWidgetOptions,
  getOpaqueVegaLiteApiWidget,
  isJsonObject,
  isUnreadablePersistedVegaLiteOptions,
  isVegaLiteWidget,
  type PersistedVegaLiteWidget,
} from '@neptune/vega-lite-widget-domain';

import { isNotUndefined } from 'common/tsHelpers';
import { AttributeDefinition, AttributeType } from 'domain/experiment/attribute';
import { extractAttributeDefinitions } from 'domain/widget/widget-model-utils';

import {
  convertWidgetOptionsFromApiToDomain,
  convertWidgetOptionsFromApiToDomainByWidgetType,
  convertWidgetOptionsFromDomainToApi,
} from './core/domain/widget-registry';
import { widgetTypeFromApiToDomain, widgetTypeFromDomainToApi } from './core/widget';

const EVALUATION_CHART_WIRE_VERSION = 1;
const EVALUATION_CHART_WIRE_NAMESPACE_PREFIX = `__neptune_evaluation_chart_v${EVALUATION_CHART_WIRE_VERSION}__:`;

type EvaluationChartWireMetadata = {
  namespace?: string;
  options: Record<string, unknown>;
};

export abstract class WidgetModelConverter {
  static widgetFromApiToDomain(widget: WidgetDTO): Widget {
    const evaluationChartWireMetadata = extractEvaluationChartWireMetadata(widget);
    const isEvaluationChart = evaluationChartWireMetadata !== undefined;
    const widgetType = isEvaluationChart
      ? 'evaluationChart'
      : widgetTypeFromApiToDomain(widget.type);
    const attributes: AttributeDefinition[] = (widget.attributes ?? []).map((attribute) => {
      if (isEvaluationChart) {
        return {
          name: attribute.name,
          type: 'evaluationSeries',
        };
      }

      return WidgetModelConverter.widgetAttributeFromApiToDomain(attribute);
    });

    const attributeSources: AttributeWidgetSource[] = attributes.map(
      ({ type, name, subproperty }) => ({
        type: WidgetSourceType.ATTRIBUTE,
        value: name,
        metadata: { attributeType: type, subproperty },
      }),
    );

    const namespaceSources: NamespaceWidgetSource[] = (widget.namespaces ?? [])
      .filter((namespace) => namespace !== evaluationChartWireMetadata?.namespace)
      .map((namespace) => ({
        type: WidgetSourceType.NAMESPACE,
        value: namespace,
      }));

    const customYExpressionsSources: CustomYExpressionWidgetSource[] = (
      widgetType === 'vegaLite' ? [] : extractCustomYExpressions(widget.options)
    ).map(({ expression, alias }) => ({
      type: WidgetSourceType.CUSTOM_Y_EXPRESSION,
      value: expression,
      metadata: { alias },
      parsedExpression: parseCustomAxis(expression),
    }));

    const attributePatternSources: AttributePatternWidgetSource[] = (
      widget.attributeNameMustMatchRegexes ?? []
    ).map((attributePattern) => ({
      type: WidgetSourceType.ATTRIBUTE_PATTERN,
      value: attributePattern,
    }));

    const textSource: TextWidgetSource | undefined =
      widget.type === 'textNode' || widget.type === 'userDefinedFunction'
        ? {
            type: WidgetSourceType.FREE_TEXT,
            value: widget.freeText,
          }
        : undefined;

    const sources = [
      ...attributeSources,
      ...namespaceSources,
      ...customYExpressionsSources,
      ...attributePatternSources,
      textSource,
    ].filter(isNotUndefined);

    const converted: PersistedVegaLiteWidget = {
      id: widget.id,
      name: widget.name,
      type: widgetType,
      options: isEvaluationChart
        ? convertWidgetOptionsFromApiToDomainByWidgetType(
            'evaluationChart',
            evaluationChartWireMetadata.options,
          )
        : convertWidgetOptionsFromApiToDomain(widget.type, widget.options),
      sources,
    };

    if (
      widgetType === 'vegaLite' &&
      (isUnreadablePersistedVegaLiteOptions(widget.options) ||
        (isJsonObject(widget.options) &&
          widget.options.version === 2 &&
          (!isVegaLiteWidget(converted) ||
            widget.attributes?.some((attribute) =>
              Object.keys(attribute).some((key) => !['name', 'type', 'subproperty'].includes(key)),
            ))))
    ) {
      converted.opaqueVegaLiteApiConfiguration = {
        widget: cloneDeep(widget),
        sourcesJson: JSON.stringify(sources),
      };
    }

    return converted;
  }

  static widgetAttributeFromApiToDomain(attribute: WidgetAttributeDTO): AttributeDefinition {
    return {
      name: attribute.name,
      subproperty: attribute.subproperty,
      type: WidgetModelConverter.widgetAttributeTypeFromApiToDomain(attribute.type),
    };
  }

  static widgetFromDomainToApi(widget: Widget): WidgetDTO | undefined {
    const opaqueWidget = getOpaqueVegaLiteApiWidget(widget);

    if (opaqueWidget) {
      const converted = cloneDeep(opaqueWidget);
      converted.id = widget.id;

      if ('name' in converted || widget.name !== undefined) {
        converted.name = widget.name;
      }

      return converted;
    }

    assertVegaLiteApiConfiguration(widget);
    const isEvaluationChart = widget.type === 'evaluationChart';
    const type = isEvaluationChart
      ? WidgetDTOTypeEnum.ScatterPlot
      : widgetTypeFromDomainToApi(widget.type);

    if (!type) {
      return undefined;
    }

    const yCustomExpressions =
      widget.type === 'vegaLite' ? [] : internalExtractYCustomExpressions(widget);

    const attributeNameMustMatchRegexes = internalExtractMustMatchAttributePatterns(widget);

    const convertedOptions = convertWidgetOptionsFromDomainToApi(widget.type, widget.options);
    const rawOptions = isEvaluationChart
      ? {
          ...convertedOptions,
          evaluationChartVersion: EVALUATION_CHART_WIRE_VERSION,
        }
      : convertedOptions;
    const options = yCustomExpressions.length > 0 ? { ...rawOptions } : rawOptions;
    const namespaces = isEvaluationChart
      ? [
          ...internalExtractNamespaces(widget),
          createEvaluationChartWireNamespace(isRecord(convertedOptions) ? convertedOptions : {}),
        ]
      : internalExtractNamespaces(widget);

    // isRecord makes typescript happy
    if (yCustomExpressions.length > 0 && isRecord(options)) {
      options.yCustomExpressions = yCustomExpressions;
    }

    const attributes = extractAttributeDefinitions(widget).map((attribute) =>
      isEvaluationChart
        ? {
            name: attribute.name,
            type: WidgetAttributeDTOTypeEnum.String,
          }
        : WidgetModelConverter.widgetAttributeFromDomainToApi(attribute),
    );

    if (widget.type === 'vegaLite' && isRecord(options) && options.version === 2) {
      return {
        id: widget.id,
        name: widget.name,
        type,
        attributes,
        options,
      };
    }

    return {
      id: widget.id,
      attributes,
      name: widget.name,
      attributeNameMustMatchRegexes,
      freeText: internalExtractFreeText(widget)[0],
      namespaces,
      options,
      type,
    };
  }

  static widgetAttributeFromDomainToApi(
    attributeDefinition: AttributeDefinition,
  ): WidgetAttributeDTO {
    return {
      ...attributeDefinition,
      type: WidgetModelConverter.widgetAttributeTypeFromDomainToApi(attributeDefinition.type),
    };
  }

  static widgetAttributeTypeFromDomainToApi(
    attributeType: AttributeType,
  ): WidgetAttributeDTOTypeEnum {
    switch (attributeType) {
      case 'bool':
        return WidgetAttributeDTOTypeEnum.Bool;

      case 'datetime':
        return WidgetAttributeDTOTypeEnum.Datetime;

      case 'experimentState':
        return WidgetAttributeDTOTypeEnum.ExperimentState;

      case 'evaluationSeries':
        return WidgetAttributeDTOTypeEnum.EvaluationSeries;

      case 'file':
        return WidgetAttributeDTOTypeEnum.File;

      case 'fileSet':
        return WidgetAttributeDTOTypeEnum.FileSet;

      case 'float':
        return WidgetAttributeDTOTypeEnum.Float;

      case 'floatSeries':
        return WidgetAttributeDTOTypeEnum.FloatSeries;

      case 'gitRef':
        return WidgetAttributeDTOTypeEnum.GitRef;

      case 'histogramSeries':
        return WidgetAttributeDTOTypeEnum.HistogramSeries;

      case 'imageSeries':
        return WidgetAttributeDTOTypeEnum.ImageSeries;

      case 'int':
        return WidgetAttributeDTOTypeEnum.Int;

      case 'string':
        return WidgetAttributeDTOTypeEnum.String;

      case 'stringSeries':
        return WidgetAttributeDTOTypeEnum.StringSeries;

      case 'stringSet':
        return WidgetAttributeDTOTypeEnum.StringSet;

      case 'fileSeries':
        return WidgetAttributeDTOTypeEnum.FileSeries;
    }

    return WidgetAttributeDTOTypeEnum.NotSupported;
  }

  static widgetAttributeTypeFromApiToDomain(
    attributeType: WidgetAttributeDTOTypeEnum,
  ): AttributeType {
    switch (attributeType) {
      case WidgetAttributeDTOTypeEnum.Bool:
        return 'bool';

      case WidgetAttributeDTOTypeEnum.Datetime:
        return 'datetime';

      case WidgetAttributeDTOTypeEnum.EvaluationSeries:
        return 'evaluationSeries';

      case WidgetAttributeDTOTypeEnum.ExperimentState:
        return 'experimentState';

      case WidgetAttributeDTOTypeEnum.File:
        return 'file';

      case WidgetAttributeDTOTypeEnum.FileSet:
        return 'fileSet';

      case WidgetAttributeDTOTypeEnum.Float:
        return 'float';

      case WidgetAttributeDTOTypeEnum.FloatSeries:
        return 'floatSeries';

      case WidgetAttributeDTOTypeEnum.GitRef:
        return 'gitRef';

      case WidgetAttributeDTOTypeEnum.HistogramSeries:
        return 'histogramSeries';

      case WidgetAttributeDTOTypeEnum.ImageSeries:
        return 'imageSeries';

      case WidgetAttributeDTOTypeEnum.Int:
        return 'int';

      case WidgetAttributeDTOTypeEnum.String:
        return 'string';

      case WidgetAttributeDTOTypeEnum.StringSeries:
        return 'stringSeries';

      case WidgetAttributeDTOTypeEnum.StringSet:
        return 'stringSet';

      case WidgetAttributeDTOTypeEnum.FileSeries:
        return 'fileSeries';
    }

    return 'notSupported';
  }
}

function isRecord(input: unknown): input is Record<string, unknown> {
  return typeof input === 'object' && input !== null;
}

function assertVegaLiteApiConfiguration(widget: Pick<Widget, 'type' | 'options' | 'sources'>) {
  if (widget.type !== 'vegaLite' || !isRecord(widget.options) || widget.options.version !== 2) {
    return;
  }

  const decoded = decodeVegaLiteWidgetOptions(widget.options);

  if (decoded.status === 'invalid') {
    throw new Error(decoded.errors.join(' '));
  }

  if (decoded.status === 'supported') {
    assertVegaLiteFloatSeriesConfiguration({ ...widget, options: decoded.options });
  }
}

function createEvaluationChartWireNamespace(options: Record<string, unknown>): string {
  return `${EVALUATION_CHART_WIRE_NAMESPACE_PREFIX}${JSON.stringify(options)}`;
}

function extractEvaluationChartWireMetadata(
  widget: WidgetDTO,
): EvaluationChartWireMetadata | undefined {
  if (widget.type !== WidgetDTOTypeEnum.ScatterPlot) {
    return undefined;
  }

  const namespace = widget.namespaces?.find((value) =>
    value.startsWith(EVALUATION_CHART_WIRE_NAMESPACE_PREFIX),
  );

  if (!namespace) {
    return extractEvaluationChartOptionsFallback(widget);
  }

  try {
    const options: unknown = JSON.parse(
      namespace.slice(EVALUATION_CHART_WIRE_NAMESPACE_PREFIX.length),
    );

    if (isRecord(options)) {
      return { namespace, options };
    }
  } catch {
    // Fall back to the redundant options payload below.
  }

  const fallback = extractEvaluationChartOptionsFallback(widget);
  return fallback ? { ...fallback, namespace } : undefined;
}

function extractEvaluationChartOptionsFallback(
  widget: WidgetDTO,
): EvaluationChartWireMetadata | undefined {
  if (
    !isRecord(widget.options) ||
    widget.options.evaluationChartVersion !== EVALUATION_CHART_WIRE_VERSION
  ) {
    return undefined;
  }

  return {
    options: Object.fromEntries(
      Object.entries(widget.options).filter(([key]) => key !== 'evaluationChartVersion'),
    ),
  };
}

function internalExtractYCustomExpressions(widget: Widget) {
  return widget.sources
    .filter(isYCustomExpressionSource)
    .map((source) => ({ expression: source.value, alias: source.metadata.alias }));
}

function internalExtractNamespaces(widget: Widget) {
  return widget.sources
    .filter((source) => source.type === WidgetSourceType.NAMESPACE)
    .map((source) => source.value);
}

function internalExtractFreeText(widget: Widget) {
  return widget.sources
    .filter((source) => source.type === WidgetSourceType.FREE_TEXT)
    .map((source) => source.value);
}

function internalExtractMustMatchAttributePatterns(widget: Widget) {
  return widget.sources.filter(isAttributePatternSource).map((source) => source.value);
}

function extractCustomYExpressions(
  options?: Record<string, any>,
): { alias?: string; expression: string }[] {
  if (!options) {
    return [];
  }

  // temporary store until BE starts recognizing widget sources
  if (
    options.yCustomExpressions &&
    Array.isArray(options.yCustomExpressions) &&
    options.yCustomExpressions.every(
      (expressionEntry) =>
        typeof expressionEntry === 'object' &&
        typeof expressionEntry?.expression === 'string' &&
        (expressionEntry?.alias === undefined || typeof expressionEntry?.alias === 'string'),
    )
  ) {
    return [...options.yCustomExpressions];
  }

  // backward compatible with old code
  // todo remove me once BE migration is done
  if (options.yCustomExpression && typeof options.yCustomExpression === 'string') {
    return [{ expression: options.yCustomExpression }];
  }

  return [];
}
